import { PeerAccountKeyring } from '../keyring/account_peer';
import { MissingKey } from '../keyring/base';
import { SelfAccountKeyring } from '../keyring/account_self';

/**
 * Reference implementation for checking and loading peer keyring based on variant level and preparing SS from KEM.
 *
 *     Keep in mind that resaving the PEER_SS is necessary after loading the peer keyring successfully independently
 *     of this procedure.
 */
export abstract class AbstractPreparePeerKeyringKEM {
  public previous_kr_variants;
  public ss_self_version;
  public ss_peer_version;
  public kem_version;

  constructor(
    previous_kr_variants: Array<string>,
    ss_self_version: string,
    ss_peer_version: string,
    kem_version: string
  ) {
    this.previous_kr_variants = previous_kr_variants;
    this.ss_self_version = ss_self_version;
    this.ss_peer_version = ss_peer_version;
    this.kem_version = kem_version;
  }

  /**
   * This can be used when a PQC payload is to be loaded to check if a SS from KEM is ready.
   */
  public check_peer_kr_kem_ready(
    peer_kr: PeerAccountKeyring,
    require_self_ss: boolean = false
  ): boolean {
    // NOTE: We do not check the variant of the self or peer keyring here:
    //       - checking the SS in the peer_kr implies it
    //       - having a KEM-related payload arrive when the self keyring is not ML capable is invalid anyway
    try {
      peer_kr.get_shared_secret(this.ss_self_version);
      return true;
    } catch (e) {
      if (e instanceof MissingKey) {
      } else {
        throw e;
      }
    }
    if (!require_self_ss) {
      try {
        peer_kr.get_shared_secret(this.ss_peer_version);
        return true;
      } catch (e) {
        if (e instanceof MissingKey) {
        } else {
          throw e;
        }
      }
    }
    return false;
  }

  public async ready_peer_kr_kem(
    self_kr: SelfAccountKeyring,
    peer_kr: PeerAccountKeyring,
    require_self_ss: boolean = false
  ): Promise<PeerAccountKeyring> {
    // NOTE: KEM can only be prepared if both accounts have at least ML variant.
    if (this.previous_kr_variants.indexOf(self_kr.get_variant()) >= 0) {
      return peer_kr;
    }
    if (this.previous_kr_variants.indexOf(peer_kr.get_variant()) >= 0) {
      peer_kr = await this._check_peer_kr_variant(self_kr, peer_kr);
      if (this.previous_kr_variants.indexOf(peer_kr.get_variant()) >= 0) {
        return peer_kr;
      }
    }
    // NOTE: Check if required/any SS is in the kr, attempt loading missing SS from the server.
    for (let i = 0; i < 2; i++) {
      try {
        peer_kr.get_shared_secret(this.ss_self_version);
        return peer_kr;
      } catch (e) {
        if (e instanceof MissingKey) {
        } else {
          throw e;
        }
      }
      if (!require_self_ss) {
        try {
          peer_kr.get_shared_secret(this.ss_peer_version);
          return peer_kr;
        } catch (e) {
          if (e instanceof MissingKey) {
          } else {
            throw e;
          }
        }
      }
      if (i === 0) {
        peer_kr = await this._check_peer_kr_ss(self_kr, peer_kr);
      }
    }
    // NOTE: Create and publish new kem.
    const tmp = await peer_kr.generate_kem(self_kr, this.kem_version);
    await this._save_kem(self_kr, peer_kr, tmp.enc_ss, tmp.kem, tmp.kem_signature);

    // NOTE: Receive new kem from server as acknowledgement.
    return await this._wait_get_peer_kr(self_kr, peer_kr);
  }

  /**
   * Check if the keyring can be upgraded and if the server has a new variant of the peer keyring.
   * This could be optional if the client keeps the peer keyring objects in sync with the event stream.
   */
  public abstract _check_peer_kr_variant(
    self_kr: SelfAccountKeyring,
    peer_kr: PeerAccountKeyring
  ): Promise<PeerAccountKeyring>;

  /**
   * Check if there are not loaded shared secrets for the peer keyring from the server.
   * This could be optional if the client keeps the peer keyring objects in sync with the event stream.
   */
  public abstract _check_peer_kr_ss(
    self_kr: SelfAccountKeyring,
    peer_kr: PeerAccountKeyring
  ): Promise<PeerAccountKeyring>;

  /**
   * Send save request to the server and wait for acknowledgement of request.
   */
  public abstract _save_kem(
    self_kr: SelfAccountKeyring,
    peer_kr: PeerAccountKeyring,
    enc_ss: Uint8Array,
    kem: Uint8Array,
    kem_sig: Uint8Array
  ): Promise<void>;

  /**
   * The client application can listen to an event stream to update the self.peer_kr and return it.
   * Specifically the relevant SS_SELF needs to be waited.
   * It can also poll and return if the required or any ss is received.
   * This shall have a time limit to not block forever by accident, but no other failure branches need to be handled.
   */
  public abstract _wait_get_peer_kr(
    self_kr: SelfAccountKeyring,
    peer_kr: PeerAccountKeyring
  ): Promise<PeerAccountKeyring>;
}

export abstract class AbstractPreparePeerKeyringMlKem extends AbstractPreparePeerKeyringKEM {
  constructor() {
    super(
      [SelfAccountKeyring.VARIANT_BASE],
      PeerAccountKeyring.SS_SELF_MLKEM1024,
      PeerAccountKeyring.SS_PEER_MLKEM1024,
      SelfAccountKeyring.MLKEM1024
    );
  }
}
